import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { chatWidget, getAccount } from "../lib/supabase.js";
import ChatWidgetPreview from "../components/ChatWidgetPreview.jsx";
import { useUserRole, hasPermission } from "../App.jsx";

const PROVIDER_MODELS = {
  openai: ["gpt-4o-mini", "gpt-4o", "gpt-4.1-mini", "gpt-4.1"],
  anthropic: ["claude-haiku-4-5-20251001", "claude-sonnet-4-6"],
  gemini: ["gemini-2.5-flash", "gemini-2.5-pro", "gemini-2.0-flash"],
};

// ─── Font loader ─────────────────────────────────────────────────────────────

function useFonts() {
  useEffect(() => {
    [
      { id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
      { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap" },
    ].forEach((f) => {
      if (!document.getElementById(f.id)) {
        const link = document.createElement("link");
        link.id = f.id; link.rel = "stylesheet"; link.href = f.href;
        document.head.appendChild(link);
      }
    });
  }, []);
}

// ─── Theme ───────────────────────────────────────────────────────────────────

const L = {
  bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444444", muted: "#8a8a8a",
  dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9",
  surface: "#ffffff", surfaceHover: "#f6f5f2", inputBg: "#f6f5f2",
};
const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

const iS = { width: "100%", padding: "9px 12px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 8, color: L.text, fontSize: 13, outline: "none", fontFamily: sans, boxSizing: "border-box" };
const lS = { display: "block", fontSize: 10, color: L.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5, fontWeight: 600 };
const secS = { marginBottom: 24, paddingBottom: 24, borderBottom: `1px solid ${L.borderLight}` };
const secHead = { fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 };
const btnGhost = { padding: "8px 16px", background: "none", border: `1px solid ${L.border}`, borderRadius: 8, color: L.muted, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: sans };

// ─── Toggle component ────────────────────────────────────────────────────────

function Toggle({ value, onChange }) {
  return (
    <button onClick={() => onChange(!value)} style={{
      width: 40, height: 22, borderRadius: 11, border: `1px solid ${value ? "#16a34a" : L.border}`,
      cursor: "pointer", background: value ? "#16a34a" : L.inputBg, position: "relative",
      transition: "all 0.2s",
    }}>
      <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: value ? 21 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }} />
    </button>
  );
}

// ─── Config constants ────────────────────────────────────────────────────────

const ANIM_OPTIONS = [
  { value: "none", label: "None" }, { value: "pulse", label: "Pulse" },
  { value: "bounce", label: "Bounce" }, { value: "spin", label: "Spin" }, { value: "wiggle", label: "Wiggle" },
];
const HOVER_OPTIONS = [
  { value: "none", label: "None" }, { value: "spin", label: "Spin" },
  { value: "scale", label: "Scale Up" }, { value: "glow", label: "Glow" },
];
const LOADING_OPTIONS = [
  { value: "dots", label: "Bouncing Dots" }, { value: "spinner", label: "Spinner" }, { value: "tire-spin", label: "Tire Spin" },
];
const MSG_ANIM_OPTIONS = [
  { value: "none", label: "None" }, { value: "fade-up", label: "Fade Up" }, { value: "slide-in", label: "Slide In" },
];

const DEFAULT_CONFIG = {
  slug: "default", name: "Chat Widget",
  primary_color: "#0154a4", secondary_color: "#013a75", background_color: "#ffffff", text_color: "#333333",
  header_gradient_from: "#0154a4", header_gradient_to: "#013a75",
  position: "bottom-right", launcher_size: 80, launcher_image_url: "", launcher_icon: "chat",
  window_width: 420, window_height: 650, border_radius: 16, font_family: "inherit", custom_css: "",
  launcher_animation: "pulse", launcher_hover_animation: "spin", loading_animation: "dots", message_animation: "fade-up",
  header_title: "AI Assistant", header_subtitle: "Ask me about our products!", greeting_message: "Hi! How can I help you find what you're looking for today?",
  suggested_questions: ["Product recommendations", "Pricing info", "Technical specs"],
  placeholder_text: "Ask about products...", powered_by_text: "Powered by Clarifico AI", show_powered_by: true,
  logo_url: "", avatar_text: "AI", close_label: "Close Chat", open_label: "Chat with us",
  ai_provider: "openai", ai_model: "gpt-4o-mini", ai_api_key: "", ai_temperature: 0.7, ai_max_tokens: 500,
  ai_system_prompt: "You are a helpful product expert. Answer questions about our products based on the catalog data provided. Be concise, friendly, and recommend specific products when relevant.",
  show_product_cards: true, card_show_image: true, card_show_sku: true, card_show_features: true,
  card_show_price: false, card_max_results: 3, card_cta_text: "View Product →", card_cta_secondary_text: "Where to Buy →",
  auto_open_delay: 0, expand_enabled: true, mobile_fullscreen: true, is_active: true,
  // ─── Lead capture defaults ──────────────────────────────────────────
  // Mirrors the JSONB default in the chat_widget_configs migration so the
  // UI works for both freshly-saved rows and historical ones with no value.
  lead_capture_config: {
    enabled: false,
    fields: { name: "required", email: "required", phone: "optional", message: "optional" },
    always_available: true,
    chip_label: "Contact me",
    auto_prompt_on_uncertainty: true,
    uncertainty_message: "I'm not sure I can help with that — would you like to leave your contact info so someone can follow up?",
    form_title: "Leave your contact info",
    form_subtitle: "We'll follow up with you shortly.",
    privacy_text: "We'll only use this to follow up. We never share your info.",
    submit_button_label: "Submit",
    confirmation_message: "Thanks! Someone from our team will be in touch shortly.",
    notify_email: null,
    notify_from_name: null,
    notify_reply_to: null,
    // Phone number the AI offers to visitors who want to speak with a person.
    // Free-text — tenant types it however they want it displayed (e.g.
    // "1-800-555-0199 ext. 200"); the AI passes it through verbatim.
    support_phone: "",
    privacy_url: "",         // required by tenant UI but allowed to be empty in DB
    retention_days: 365,     // 1 year default; rows older than this are auto-deleted
  },
};

const sections = [
  { key: "appearance", label: "Appearance" },
  { key: "animations", label: "Animations" },
  { key: "content", label: "Content" },
  { key: "ai", label: "AI settings" },
  { key: "lead_capture", label: "Lead capture" },
  { key: "cards", label: "Product cards" },
  { key: "embed", label: "Embed" },
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function Widget() {
  useFonts();
  const account = getAccount();
  const color = account?.widget_color || "#6366f1";
  const { role } = useUserRole();
  const canEdit = hasPermission(role, "editContent");
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [configId, setConfigId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("appearance");
  const [sugInput, setSugInput] = useState("");
  const [embedCopied, setEmbedCopied] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await chatWidget.get("default");
      if (data) { setConfig({ ...DEFAULT_CONFIG, ...data }); setConfigId(data.id); }
      setLoading(false);
    })();
  }, []);

  const set = (k, v) => setConfig((p) => ({ ...p, [k]: v }));
  // setLc — update a single key inside the lead_capture_config JSONB without
  // clobbering other keys, with defaults merged in so callers can edit any
  // field even if a saved row is missing it.
  const setLc = (k, v) =>
    setConfig((p) => ({
      ...p,
      lead_capture_config: { ...DEFAULT_CONFIG.lead_capture_config, ...(p.lead_capture_config || {}), [k]: v },
    }));
  const setLcField = (fieldKey, v) =>
    setConfig((p) => ({
      ...p,
      lead_capture_config: {
        ...DEFAULT_CONFIG.lead_capture_config,
        ...(p.lead_capture_config || {}),
        fields: {
          ...DEFAULT_CONFIG.lead_capture_config.fields,
          ...((p.lead_capture_config || {}).fields || {}),
          [fieldKey]: v,
        },
      },
    }));
  // Convenience accessor — always reads through defaults so we never crash on
  // missing keys from older saved rows.
  const lc = { ...DEFAULT_CONFIG.lead_capture_config, ...(config.lead_capture_config || {}) };

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    // Privacy URL is required when lead capture is enabled. We made this a
    // contract with the tenant: collect PII → tell visitors how it's used.
    const lcCur = { ...DEFAULT_CONFIG.lead_capture_config, ...(config.lead_capture_config || {}) };
    if (lcCur.enabled && !lcCur.privacy_url?.trim()) {
      setMsg({ type: "error", text: "Privacy policy URL is required when lead capture is on. Add one in the Lead capture section." });
      setActiveSection("lead_capture");
      setSaving(false);
      return;
    }
    // Soft check: notify_email warning happens inline (UI surfaces it),
    // not as a save-block — tenants might intentionally use dashboard-only.
    const payload = { ...config }; delete payload.id; delete payload.created_at;
    let result;
    if (configId) result = await chatWidget.update(configId, payload);
    else result = await chatWidget.upsertBySlug("default", payload);
    if (result.error) setMsg({ type: "error", text: result.error.message });
    else { setMsg({ type: "success", text: "Saved!" }); if (result.data?.id) setConfigId(result.data.id); setTimeout(() => setMsg(null), 3000); }
    setSaving(false);
  };

  const addSuggestion = () => { if (!sugInput.trim()) return; set("suggested_questions", [...(config.suggested_questions || []), sugInput.trim()]); setSugInput(""); };
  const removeSuggestion = (i) => set("suggested_questions", (config.suggested_questions || []).filter((_, j) => j !== i));

  const embedCode = `<!-- Clarifico Chat Widget -->
<script
  src="https://cdn.clarifico.io/widget-chat.js"
  data-account="${account?.slug || "your-slug"}"
  data-config="${config.slug || "default"}"
  data-api="https://amtdvoginmkwzbkwrdld.supabase.co/functions/v1"
  async>
</script>`;
  const copyEmbed = () => { navigator.clipboard.writeText(embedCode); setEmbedCopied(true); setTimeout(() => setEmbedCopied(false), 2000); };

  if (!canEdit) return <Navigate to="/" />;
  if (loading) return <Layout><div style={{ padding: 40, textAlign: "center", color: L.muted, background: L.bg, margin: -28, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: sans }}>Loading widget config...</div></Layout>;

  return (
    <Layout>
      <style>{`@keyframes ov-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      <div style={{ minHeight: "calc(100vh - 56px)", background: L.bg, margin: -28, padding: "40px 44px", fontFamily: sans, color: L.text }}>
        {/* ── Header ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, animation: "ov-up 0.5s ease both" }}>
          <div>
            <h1 style={{ fontSize: 36, fontWeight: 400, lineHeight: 1.1, margin: "0 0 8px", fontFamily: serif, fontStyle: "italic" }}>Chat Widget</h1>
            <p style={{ margin: 0, fontSize: 13, color: L.muted }}>Configure and preview your embeddable chat widget</p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {msg && (
              <div style={{
                padding: "6px 14px", borderRadius: 8, fontSize: 12,
                background: msg.type === "error" ? "#fef2f2" : "#f0fdf4",
                color: msg.type === "error" ? "#dc2626" : "#15803d",
                border: `1px solid ${msg.type === "error" ? "#fecaca" : "#bbf7d0"}`,
              }}>{msg.text}</div>
            )}
            <button onClick={handleSave} disabled={saving} style={{
              padding: "9px 22px", background: L.text, border: "none", borderRadius: 8,
              color: L.bg, fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
              fontFamily: sans, opacity: saving ? 0.5 : 1,
            }}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {/* ── Layout: config + preview ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: previewExpanded ? "1fr" : "1fr 360px",
          gap: 24, alignItems: "start",
          animation: "ov-up 0.5s ease 0.08s both",
        }}>
          {/* Config panel */}
          {!previewExpanded && (
            <div>
              {/* Section tabs */}
              <div style={{ display: "flex", gap: 4, marginBottom: 18, flexWrap: "wrap" }}>
                {sections.map((s) => {
                  const active = activeSection === s.key;
                  return (
                    <button key={s.key} onClick={() => setActiveSection(s.key)} style={{
                      padding: "7px 14px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                      cursor: "pointer", fontFamily: sans, transition: "all 0.15s",
                      background: active ? L.text : "none",
                      color: active ? L.bg : L.muted,
                      border: `1px solid ${active ? L.text : L.border}`,
                    }}>
                      {s.label}
                    </button>
                  );
                })}
              </div>

              <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, padding: 26 }}>

                {/* ─── APPEARANCE ─── */}
                {activeSection === "appearance" && (<>
                  <div style={secS}>
                    <div style={secHead}>Colors</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                      {[
                        ["primary_color", "Primary"], ["secondary_color", "Secondary"], ["background_color", "Background"],
                        ["text_color", "Text"], ["header_gradient_from", "Header from"], ["header_gradient_to", "Header to"],
                      ].map(([key, label]) => (
                        <div key={key}>
                          <label style={lS}>{label}</label>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input type="color" value={config[key] || "#000000"} onChange={(e) => set(key, e.target.value)} style={{ width: 32, height: 32, border: `1px solid ${L.border}`, borderRadius: 6, cursor: "pointer", background: "none", padding: 2 }} />
                            <span style={{ fontSize: 11, color: L.muted, fontFamily: "monospace" }}>{config[key]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={secS}>
                    <div style={secHead}>Launcher</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div><label style={lS}>Icon type</label><select value={config.launcher_icon} onChange={(e) => set("launcher_icon", e.target.value)} style={{ ...iS, cursor: "pointer" }}><option value="chat">Default Chat Icon</option><option value="custom">Custom Image</option></select></div>
                      <div><label style={lS}>Size (px)</label><input type="number" value={config.launcher_size} onChange={(e) => set("launcher_size", parseInt(e.target.value) || 60)} style={iS} /></div>
                    </div>
                    {config.launcher_icon === "custom" && (
                      <div style={{ marginTop: 14 }}>
                        <label style={lS}>Launcher image URL</label>
                        <input value={config.launcher_image_url} onChange={(e) => set("launcher_image_url", e.target.value)} placeholder="https://example.com/icon.png" style={iS} />
                        {config.launcher_image_url && <div style={{ marginTop: 8, width: 56, height: 56, borderRadius: 8, overflow: "hidden", border: `1px solid ${L.borderLight}` }}><img src={config.launcher_image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} /></div>}
                      </div>
                    )}
                  </div>

                  <div style={secS}>
                    <div style={secHead}>Window</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                      <div><label style={lS}>Position</label><select value={config.position} onChange={(e) => set("position", e.target.value)} style={{ ...iS, cursor: "pointer" }}><option value="bottom-right">Bottom Right</option><option value="bottom-left">Bottom Left</option></select></div>
                      <div><label style={lS}>Width (px)</label><input type="number" value={config.window_width} onChange={(e) => set("window_width", parseInt(e.target.value) || 420)} style={iS} /></div>
                      <div><label style={lS}>Height (px)</label><input type="number" value={config.window_height} onChange={(e) => set("window_height", parseInt(e.target.value) || 650)} style={iS} /></div>
                      <div><label style={lS}>Border radius</label><input type="number" value={config.border_radius} onChange={(e) => set("border_radius", parseInt(e.target.value) || 16)} style={iS} /></div>
                      <div><label style={lS}>Font family</label><input value={config.font_family} onChange={(e) => set("font_family", e.target.value)} placeholder="inherit" style={iS} /></div>
                    </div>
                  </div>

                  <div>
                    <div style={secHead}>Branding</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div><label style={lS}>Logo URL</label><input value={config.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://..." style={iS} /></div>
                      <div><label style={lS}>Avatar text</label><input value={config.avatar_text} onChange={(e) => set("avatar_text", e.target.value)} maxLength={3} style={iS} /></div>
                    </div>
                  </div>
                </>)}

                {/* ─── ANIMATIONS ─── */}
                {activeSection === "animations" && (<>
                  <div style={secS}>
                    <div style={secHead}>Launcher button</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div><label style={lS}>Idle animation</label><select value={config.launcher_animation} onChange={(e) => set("launcher_animation", e.target.value)} style={{ ...iS, cursor: "pointer" }}>{ANIM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                      <div><label style={lS}>Hover animation</label><select value={config.launcher_hover_animation} onChange={(e) => set("launcher_hover_animation", e.target.value)} style={{ ...iS, cursor: "pointer" }}>{HOVER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                    </div>
                    <div style={{ marginTop: 10, fontSize: 12, color: L.muted, lineHeight: 1.6 }}>Close the chat in the preview to see launcher animations. Hover over the button to see hover effects.</div>
                  </div>
                  <div>
                    <div style={secHead}>Chat window</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div><label style={lS}>Loading animation</label><select value={config.loading_animation} onChange={(e) => set("loading_animation", e.target.value)} style={{ ...iS, cursor: "pointer" }}>{LOADING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                      <div><label style={lS}>Message animation</label><select value={config.message_animation} onChange={(e) => set("message_animation", e.target.value)} style={{ ...iS, cursor: "pointer" }}>{MSG_ANIM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                    </div>
                  </div>
                </>)}

                {/* ─── CONTENT ─── */}
                {activeSection === "content" && (<>
                  <div style={secS}>
                    <div style={secHead}>Header</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div><label style={lS}>Title</label><input value={config.header_title} onChange={(e) => set("header_title", e.target.value)} style={iS} /></div>
                      <div><label style={lS}>Subtitle</label><input value={config.header_subtitle} onChange={(e) => set("header_subtitle", e.target.value)} style={iS} /></div>
                    </div>
                  </div>

                  <div style={secS}>
                    <div style={secHead}>Messages</div>
                    <div style={{ marginBottom: 14 }}><label style={lS}>Greeting message</label><textarea value={config.greeting_message} onChange={(e) => set("greeting_message", e.target.value)} rows={3} style={{ ...iS, resize: "vertical", lineHeight: 1.6 }} /></div>
                    <div style={{ marginBottom: 14 }}><label style={lS}>Input placeholder</label><input value={config.placeholder_text} onChange={(e) => set("placeholder_text", e.target.value)} style={iS} /></div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div><label style={lS}>Open label</label><input value={config.open_label} onChange={(e) => set("open_label", e.target.value)} style={iS} /></div>
                      <div><label style={lS}>Close label</label><input value={config.close_label} onChange={(e) => set("close_label", e.target.value)} style={iS} /></div>
                    </div>
                  </div>

                  <div style={secS}>
                    <div style={secHead}>Suggested questions</div>
                    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                      <input value={sugInput} onChange={(e) => setSugInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSuggestion()} placeholder="Add a suggested question..." style={{ ...iS, flex: 1 }} />
                      <button onClick={addSuggestion} style={{ ...btnGhost, padding: "8px 14px" }}>+</button>
                    </div>
                    {(config.suggested_questions || []).map((q, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", background: L.inputBg, borderRadius: 8, marginBottom: 4, border: `1px solid ${L.borderLight}` }}>
                        <span style={{ flex: 1, fontSize: 12 }}>{q}</span>
                        <button onClick={() => removeSuggestion(i)} style={{ background: "none", border: "none", color: L.dim, cursor: "pointer", fontSize: 13 }}>×</button>
                      </div>
                    ))}
                  </div>

                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={secHead}>Show "Powered by"</div>
                      <Toggle value={config.show_powered_by} onChange={(v) => set("show_powered_by", v)} />
                    </div>
                    {config.show_powered_by && <div style={{ marginTop: 10 }}><label style={lS}>Powered by text</label><input value={config.powered_by_text} onChange={(e) => set("powered_by_text", e.target.value)} style={iS} /></div>}
                  </div>
                </>)}

                {/* ─── AI SETTINGS ─── */}
                {activeSection === "ai" && (<>
                  <div style={secS}>
                    <div style={secHead}>AI provider</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div><label style={lS}>Provider</label><select value={config.ai_provider} onChange={(e) => { const p = e.target.value; set("ai_provider", p); set("ai_model", (PROVIDER_MODELS[p] || [])[0] || ""); }} style={{ ...iS, cursor: "pointer" }}><option value="openai">OpenAI</option><option value="anthropic">Anthropic</option><option value="gemini">Gemini</option></select></div>
                      <div><label style={lS}>Model</label><select value={config.ai_model} onChange={(e) => set("ai_model", e.target.value)} style={{ ...iS, cursor: "pointer" }}>{(PROVIDER_MODELS[config.ai_provider] || []).concat(PROVIDER_MODELS[config.ai_provider]?.includes(config.ai_model) ? [] : [config.ai_model]).filter(Boolean).map((m) => <option key={m} value={m}>{m}</option>)}</select></div>
                    </div>
                    <div style={{ marginTop: 14 }}><label style={lS}>API key</label><input type="password" value={config.ai_api_key} onChange={(e) => set("ai_api_key", e.target.value)} placeholder="Leave blank for default" style={{ ...iS, fontFamily: "monospace", fontSize: 12 }} /></div>
                  </div>

                  <div style={secS}>
                    <div style={secHead}>Parameters</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div><label style={lS}>Temperature</label><div style={{ display: "flex", alignItems: "center", gap: 10 }}><input type="range" min="0" max="2" step="0.1" value={config.ai_temperature} onChange={(e) => set("ai_temperature", parseFloat(e.target.value))} style={{ flex: 1 }} /><span style={{ fontSize: 12, color: L.muted, fontFamily: "monospace", width: 30 }}>{config.ai_temperature}</span></div></div>
                      <div><label style={lS}>Max tokens</label><input type="number" value={config.ai_max_tokens} onChange={(e) => set("ai_max_tokens", parseInt(e.target.value) || 500)} style={iS} /></div>
                    </div>
                  </div>

                  <div>
                    <div style={secHead}>System prompt</div>
                    <textarea value={config.ai_system_prompt} onChange={(e) => set("ai_system_prompt", e.target.value)} rows={6} style={{ ...iS, resize: "vertical", lineHeight: 1.6 }} />
                    <div style={{ fontSize: 12, color: L.muted, marginTop: 8, lineHeight: 1.6 }}>This prompt instructs the AI how to behave. The product catalog is automatically appended as context.</div>
                  </div>
                </>)}

                {/* ─── LEAD CAPTURE ─── */}
                {activeSection === "lead_capture" && (<>
                  {/* Master toggle */}
                  <div style={secS}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
                      <div style={secHead}>Enable lead capture</div>
                      <Toggle value={lc.enabled} onChange={(v) => setLc("enabled", v)} />
                    </div>
                    <div style={{ fontSize: 12, color: L.muted, lineHeight: 1.6 }}>
                      Lets visitors leave name, email, and other info so your team can follow up. Submissions appear in the Leads page.
                    </div>
                    {/* Sensitive-data warning — always visible so tenants understand the boundary */}
                    <div style={{ marginTop: 12, padding: "10px 12px", background: "#fef3c7", border: `1px solid #fcd34d`, borderRadius: 8, fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
                      <strong>Don't use lead capture to collect sensitive information.</strong> This feature is not designed for protected health information (HIPAA), financial account details (GLBA), or any data subject to special regulatory protection. Tenants are responsible for ensuring their use of this feature complies with applicable laws.
                    </div>
                  </div>

                  {lc.enabled && (<>
                    {/* Triggers */}
                    <div style={secS}>
                      <div style={secHead}>When to show</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                        <div style={{ fontSize: 13, flex: 1 }}>
                          <div>Show a "contact me" chip in the greeting</div>
                          <div style={{ fontSize: 11, color: L.muted, marginTop: 2 }}>Visitors can request follow-up at any time.</div>
                        </div>
                        <Toggle value={lc.always_available} onChange={(v) => setLc("always_available", v)} />
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                        <div style={{ fontSize: 13, flex: 1 }}>
                          <div>Offer the form when the AI is uncertain</div>
                          <div style={{ fontSize: 11, color: L.muted, marginTop: 2 }}>Auto-prompts when the AI can't confidently answer a question.</div>
                        </div>
                        <Toggle value={lc.auto_prompt_on_uncertainty} onChange={(v) => setLc("auto_prompt_on_uncertainty", v)} />
                      </div>
                      <div>
                        <label style={lS}>Chip label</label>
                        <input value={lc.chip_label} onChange={(e) => setLc("chip_label", e.target.value)} style={iS} placeholder="Contact me" />
                      </div>
                    </div>

                    {/* Human contact — phone the AI shares with visitors who
                        want to speak with a person. Lives here because it's
                        part of the same human-handoff flow as the form itself. */}
                    <div style={secS}>
                      <div style={secHead}>Human contact</div>
                      <div style={{ fontSize: 12, color: L.muted, marginBottom: 12, lineHeight: 1.6 }}>
                        Phone number the AI offers when visitors ask to speak with someone. The AI shares it verbatim — type it exactly as you want it to appear.
                      </div>
                      <div>
                        <label style={lS}>Support phone</label>
                        <input
                          value={lc.support_phone || ""}
                          onChange={(e) => setLc("support_phone", e.target.value)}
                          placeholder="1-866-624-2400"
                          style={iS}
                        />
                        <div style={{ fontSize: 11, color: L.muted, marginTop: 4, lineHeight: 1.6 }}>
                          Leave blank if you only want visitors to use the contact form.
                        </div>
                      </div>
                    </div>

                    {/* Fields */}
                    <div style={secS}>
                      <div style={secHead}>Form fields</div>
                      <div style={{ fontSize: 12, color: L.muted, marginBottom: 12, lineHeight: 1.6 }}>
                        Required fields must be filled out before submission. Optional fields are shown but blank submissions are allowed. Off hides the field entirely.
                      </div>
                      {["name", "email", "phone", "message"].map((fk) => (
                        <div key={fk} style={{ display: "grid", gridTemplateColumns: "120px 1fr", alignItems: "center", gap: 14, marginBottom: 10 }}>
                          <label style={{ ...lS, marginBottom: 0, textTransform: "capitalize" }}>{fk}</label>
                          <select
                            value={lc.fields?.[fk] || "optional"}
                            onChange={(e) => setLcField(fk, e.target.value)}
                            style={iS}
                          >
                            <option value="required">Required</option>
                            <option value="optional">Optional</option>
                            <option value="off">Off</option>
                          </select>
                        </div>
                      ))}
                    </div>

                    {/* Form copy */}
                    <div style={secS}>
                      <div style={secHead}>Form copy</div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={lS}>Form title</label>
                        <input value={lc.form_title} onChange={(e) => setLc("form_title", e.target.value)} style={iS} />
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={lS}>Form subtitle</label>
                        <input value={lc.form_subtitle} onChange={(e) => setLc("form_subtitle", e.target.value)} style={iS} />
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={lS}>Submit button label</label>
                        <input value={lc.submit_button_label} onChange={(e) => setLc("submit_button_label", e.target.value)} style={iS} />
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={lS}>Uncertainty message</label>
                        <textarea value={lc.uncertainty_message} onChange={(e) => setLc("uncertainty_message", e.target.value)} rows={2} style={{ ...iS, resize: "vertical", lineHeight: 1.6 }} />
                        <div style={{ fontSize: 11, color: L.muted, marginTop: 4 }}>Shown when the AI auto-prompts the form.</div>
                      </div>
                      <div>
                        <label style={lS}>Confirmation message</label>
                        <textarea value={lc.confirmation_message} onChange={(e) => setLc("confirmation_message", e.target.value)} rows={2} style={{ ...iS, resize: "vertical", lineHeight: 1.6 }} />
                        <div style={{ fontSize: 11, color: L.muted, marginTop: 4 }}>Shown after a successful submission.</div>
                      </div>
                    </div>

                    {/* Privacy */}
                    <div style={secS}>
                      <div style={secHead}>Privacy <span style={{ color: "#dc2626", textTransform: "none", fontWeight: 500, marginLeft: 6 }}>required</span></div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={lS}>Privacy policy URL</label>
                        <input
                          type="url"
                          value={lc.privacy_url}
                          onChange={(e) => setLc("privacy_url", e.target.value)}
                          placeholder="https://yoursite.com/privacy"
                          style={{ ...iS, borderColor: lc.enabled && !lc.privacy_url?.trim() ? "#dc2626" : L.border }}
                        />
                        <div style={{ fontSize: 11, color: lc.enabled && !lc.privacy_url?.trim() ? "#dc2626" : L.muted, marginTop: 4, lineHeight: 1.6 }}>
                          Visitors see this link in the form. Required when collecting personal data — your tenants need to know how their info will be handled.
                        </div>
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={lS}>Privacy notice text</label>
                        <textarea value={lc.privacy_text} onChange={(e) => setLc("privacy_text", e.target.value)} rows={2} style={{ ...iS, resize: "vertical", lineHeight: 1.6 }} />
                        <div style={{ fontSize: 11, color: L.muted, marginTop: 4 }}>Shown below the form. Keep it short — the link does the heavy lifting.</div>
                      </div>
                      <div>
                        <label style={lS}>Data retention</label>
                        <select value={lc.retention_days} onChange={(e) => setLc("retention_days", parseInt(e.target.value, 10))} style={iS}>
                          <option value={90}>90 days</option>
                          <option value={180}>180 days</option>
                          <option value={365}>1 year (recommended)</option>
                          <option value={730}>2 years</option>
                          <option value={1095}>3 years</option>
                          <option value={0}>Indefinite (not recommended)</option>
                        </select>
                        <div style={{ fontSize: 11, color: L.muted, marginTop: 4, lineHeight: 1.6 }}>
                          Leads older than this are automatically deleted. Shorter retention reduces your exposure if data is ever compromised.
                        </div>
                      </div>
                    </div>

                    {/* Notification email */}
                    <div style={secS}>
                      <div style={secHead}>Email notifications</div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={lS}>Notification email</label>
                        <input
                          type="email"
                          value={lc.notify_email || ""}
                          onChange={(e) => setLc("notify_email", e.target.value || null)}
                          placeholder="leads@yourcompany.com"
                          style={iS}
                        />
                        {!lc.notify_email && (
                          <div style={{ fontSize: 11, color: L.muted, marginTop: 4, lineHeight: 1.6 }}>
                            No email set — leads will only appear in the dashboard. Add an address to get notified when new leads arrive.
                          </div>
                        )}
                      </div>
                      {lc.notify_email && (<>
                        <div style={{ marginBottom: 14 }}>
                          <label style={lS}>Display name (From)</label>
                          <input
                            value={lc.notify_from_name || ""}
                            onChange={(e) => setLc("notify_from_name", e.target.value || null)}
                            placeholder="MORryde Leads"
                            style={iS}
                          />
                          <div style={{ fontSize: 11, color: L.muted, marginTop: 4, lineHeight: 1.6 }}>
                            Shown as the sender name. Emails are sent from info@updates.clarifico.io; this label appears alongside it in the recipient's inbox.
                          </div>
                        </div>
                        <div>
                          <label style={lS}>Reply-to address</label>
                          <input
                            type="email"
                            value={lc.notify_reply_to || ""}
                            onChange={(e) => setLc("notify_reply_to", e.target.value || null)}
                            placeholder={lc.notify_email}
                            style={iS}
                          />
                          <div style={{ fontSize: 11, color: L.muted, marginTop: 4, lineHeight: 1.6 }}>
                            When you reply to the notification, your message goes here. Leave blank to use the notification email above.
                          </div>
                        </div>
                      </>)}
                    </div>
                  </>)}
                </>)}

                {/* ─── PRODUCT CARDS ─── */}
                {activeSection === "cards" && (<>
                  <div style={secS}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                      <div style={secHead}>Show product cards</div>
                      <Toggle value={config.show_product_cards} onChange={(v) => set("show_product_cards", v)} />
                    </div>
                    {config.show_product_cards && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        <div><label style={lS}>Max results</label><input type="number" min={1} max={10} value={config.card_max_results} onChange={(e) => set("card_max_results", parseInt(e.target.value) || 3)} style={iS} /></div>
                        <div><label style={lS}>CTA text</label><input value={config.card_cta_text} onChange={(e) => set("card_cta_text", e.target.value)} style={iS} /></div>
                        <div><label style={lS}>Secondary CTA</label><input value={config.card_cta_secondary_text} onChange={(e) => set("card_cta_secondary_text", e.target.value)} style={iS} /></div>
                      </div>
                    )}
                  </div>
                  {config.show_product_cards && (
                    <div>
                      <div style={secHead}>Card fields</div>
                      {[
                        ["card_show_image", "Show image"], ["card_show_sku", "Show SKU"],
                        ["card_show_features", "Show features"], ["card_show_price", "Show price"],
                      ].map(([key, label]) => (
                        <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${L.borderLight}` }}>
                          <span style={{ fontSize: 13, color: L.textSoft }}>{label}</span>
                          <Toggle value={config[key]} onChange={(v) => set(key, v)} />
                        </div>
                      ))}
                    </div>
                  )}
                </>)}

                {/* ─── EMBED ─── */}
                {activeSection === "embed" && (<>
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                      <div style={secHead}>Embed code</div>
                      <button onClick={copyEmbed} style={{
                        ...btnGhost, padding: "5px 12px", fontSize: 11,
                        color: embedCopied ? "#15803d" : L.text,
                        borderColor: embedCopied ? "#bbf7d0" : L.border,
                        background: embedCopied ? "#f0fdf4" : "none",
                      }}>
                        {embedCopied ? "Copied" : "Copy code"}
                      </button>
                    </div>
                    <pre style={{ margin: 0, padding: 16, background: L.text, borderRadius: 8, color: "#a5b4fc", fontSize: 11, fontFamily: "'DM Mono', monospace", overflowX: "auto", lineHeight: 1.7, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{embedCode}</pre>
                    <div style={{ marginTop: 10, fontSize: 12, color: L.muted, lineHeight: 1.6, padding: "10px 12px", background: L.inputBg, borderRadius: 8, border: `1px solid ${L.borderLight}` }}>
                      The widget loads all configuration from the server at runtime. After saving, the embedded widget automatically reflects your changes.
                    </div>
                  </div>

                  <div>
                    <div style={secHead}>Behavior</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                      <div><label style={lS}>Auto-open delay (ms)</label><input type="number" value={config.auto_open_delay} onChange={(e) => set("auto_open_delay", parseInt(e.target.value) || 0)} placeholder="0 = disabled" style={iS} /></div>
                    </div>
                    {[
                      ["expand_enabled", "Allow expand view"], ["mobile_fullscreen", "Mobile fullscreen"],
                    ].map(([key, label]) => (
                      <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${L.borderLight}` }}>
                        <span style={{ fontSize: 13, color: L.textSoft }}>{label}</span>
                        <Toggle value={config[key]} onChange={(v) => set(key, v)} />
                      </div>
                    ))}
                    {config.custom_css !== undefined && (
                      <div style={{ marginTop: 16 }}>
                        <label style={lS}>Custom CSS</label>
                        <textarea value={config.custom_css} onChange={(e) => set("custom_css", e.target.value)} rows={4} placeholder=".morryde-chatbot { ... }" style={{ ...iS, fontFamily: "monospace", fontSize: 11, resize: "vertical" }} />
                      </div>
                    )}
                  </div>
                </>)}
              </div>
            </div>
          )}

          {/* Preview panel */}
          <div style={{ position: previewExpanded ? "relative" : "sticky", top: previewExpanded ? 0 : 28 }}>
            <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  Live preview {previewExpanded && <span style={{ fontWeight: 400, textTransform: "none", color: "#b45309", fontSize: 10, marginLeft: 6 }}>expanded</span>}
                </div>
                {previewExpanded && <button onClick={() => setPreviewExpanded(false)} style={{ ...btnGhost, padding: "4px 12px", fontSize: 11 }}>← Back to settings</button>}
              </div>
              <ChatWidgetPreview config={config} onExpandChange={setPreviewExpanded} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
